import axios from 'axios';
import {
  API_CONFIG,
  API_ENDPOINTS,
  API_RESPONSE_STRUCTURE,
} from "../constants";

/**
 * Core API function using axios with URL-encoded form data
 * @param {string} endpoint - API endpoint name
 * @param {object} params - Parameters to send
 * @returns {Promise} API response
 */
const post = async (endpoint, params) => {
  try {
    const url = `${API_CONFIG.BASE_URL}/${endpoint}`;
    const data = new URLSearchParams();
    
    // Add all parameters to URLSearchParams
    if (params) {
      Object.keys(params).forEach(key => {
        data.append(key, params[key]);
      });
    }

    const response = await axios.post(url, data.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      timeout: API_CONFIG.TIMEOUT
    });

    // Parse XML response to extract JSON
    const xmlString = response.data;
    let jsonString = '';
    
    const startIndex = xmlString.indexOf('<string xmlns="http://tempuri.org/">');
    const endIndex = xmlString.indexOf('</string>');

    if (startIndex !== -1 && endIndex !== -1) {
      // Extract the content between the <string> tags
      jsonString = xmlString.substring(startIndex + '<string xmlns="http://tempuri.org/">'.length, endIndex);
    } else {
      throw new Error('Could not find the expected <string> tag in the XML response.');
    }

    // Parse the JSON string
    const parsedData = JSON.parse(jsonString);
    
    return {
      data: parsedData,
      error: false
    };

  } catch (error) {
    console.error('API Error:', error);
    return {
      error: true,
      message: error.message || 'Request failed',
      status: error.response?.status || 0
    };
  }
};

/**
 * Parse API response based on standard structure
 * @param {object} response - Raw API response
 * @returns {object} Parsed response with status, message, and data
 */
export const parseApiResponse = (response) => {
  if (!response || response.error) {
    return {
      success: false,
      message: response?.message || "API call failed",
      data: null,
    };
  }

  const { data } = response;
  if (!Array.isArray(data) || data.length < 3) {
    return {
      success: false,
      message: "Invalid API response format",
      data: null,
    };
  }

  const status = data[API_RESPONSE_STRUCTURE.STATUS_INDEX]?.Status;
  const message = data[API_RESPONSE_STRUCTURE.MESSAGE_INDEX]?.Message || "";
  const responseData = data[API_RESPONSE_STRUCTURE.DATA_INDEX]?.Data;

  return {
    success: status === API_RESPONSE_STRUCTURE.SUCCESS_STATUS,
    message,
    data: responseData ? JSON.parse(responseData) : null,
  };
};

// Specific API functions
export const authService = {
  /**
   * Login user
   * @param {string} userName - Username
   * @param {string} password - Password
   * @returns {Promise} Login response
   */
  login: async (userName, password) => {
    const response = await post(API_ENDPOINTS.CHECK_LOGIN, {
      keyPass: API_CONFIG.KEY_PASS,
      userName: userName,
      passWord: password,
    });
    return parseApiResponse(response);
  },
  
  /**
   * Change user password
   * @param {string} userName - Username
   * @param {string} password - New password
   * @returns {Promise} Password change response
   */
  changePassword: async (userName, password) => {
    const response = await post(API_ENDPOINTS.CHANGE_PASSWORD, {
      keyPass: API_CONFIG.KEY_PASS,
      userName: userName,
      passWord: password,
    });
    return parseApiResponse(response);
  },
};

export const shipmentService = {
  /**
   * Update shipment status with image upload capability
   * @param {number} idChuyenXe - Shipment ID
   * @param {number} trangthai - New status
   * @param {string} userName - User name making the update
   * @param {Array} images - Optional array of uploaded image IDs
   * @returns {Promise} Update response
   */
  updateStatusWithImages: async (idChuyenXe, trangthai, userName = '', images = []) => {
    // First update the status
    const statusResult = await shipmentService.updateStatus(idChuyenXe, trangthai, userName);
    
    if (!statusResult.success) {
      return statusResult;
    }

    // If images are provided and status is COMPLETED, link images to shipment
    if (images.length > 0 && trangthai === 5) {
      // Note: This would require additional API endpoint to link images to shipment
      // For now, we'll just return the status update result
      console.log('Images uploaded for shipment:', idChuyenXe, images);
    }

    return statusResult;
  },

  /**
   * Get shipments for driver
   * @param {number} idLaiXe - Driver ID
   * @param {string} tungay - From date (DD-MM-YYYY)
   * @param {string} denngay - To date (DD-MM-YYYY)
   * @returns {Promise} Shipments response
   */
  getShipmentsForDriver: async (idLaiXe, tungay, denngay) => {
    const response = await post(API_ENDPOINTS.GET_SHIPMENT_DRIVER, {
      keyPass: API_CONFIG.KEY_PASS,
      idLaiXe: idLaiXe,
      tungay: tungay,
      denngay: denngay,
    });
    return parseApiResponse(response);
  },

  /**
   * Get shipment detail
   * @param {number} idChuyenXe - Shipment ID
   * @returns {Promise} Shipment detail response
   */
  getShipmentDetail: async (idChuyenXe) => {
    const response = await post(API_ENDPOINTS.GET_SHIPMENT_DETAIL, {
      keyPass: API_CONFIG.KEY_PASS,
      idChuyenXe: idChuyenXe,
    });
    return parseApiResponse(response);
  },

  /**
   * Update shipment status
   * @param {number} idChuyenXe - Shipment ID
   * @param {number} trangthai - New status
   * @param {string} userName - User name making the update
   * @returns {Promise} Update response
   */
  updateStatus: async (idChuyenXe, trangthai, userName = '') => {
    const response = await post(API_ENDPOINTS.UPDATE_STATUS, {
      keyPass: API_CONFIG.KEY_PASS,
      idChuyenXe: idChuyenXe,
      trangthai: trangthai,
      userName: userName,
    });
    return parseApiResponse(response);
  },

  /**
   * Save shipment (create new or update existing)
   * @param {object} shipmentData - Shipment data
   * @returns {Promise} Save response
   */
  saveShipment: async (shipmentData) => {
  // Prepare parameters for SAVE_TruckRoute API
  const params = {
    keyPass: API_CONFIG.KEY_PASS,
    idChuyenXe: shipmentData.idChuyenXe || '0', // 0 for new shipment
    ngaynhap: shipmentData.ngaynhap || '',
    ngaynhan: shipmentData.ngaynhan || '',
    ngaygiao1: shipmentData.ngaygiao1 || '',
    ngaygiao2: shipmentData.ngaygiao2 || '',
    ngaygiao3: shipmentData.ngaygiao3 || '',
    ngaygiao4: shipmentData.ngaygiao4 || '',
    loaixetai_fk : shipmentData.loaixetai_fk || 0, // This field is deprecated, use loaivantai instead
    // FIX: Change from loaixetai_fk to loaivantai
    loaivantai: shipmentData.loaivantai || '1', // Default to '1' if not provided
    idTrongTai: shipmentData.idTrongTai || '',
    idXe: shipmentData.idXe || '',
    idLaiXe: shipmentData.idLaiXe || '',
    idKhachHang: shipmentData.idKhachHang || shipmentData.idKhachHangNhan || '', // Required field
    idKhachHangNhan: shipmentData.idKhachHangNhan || '',
    idKhachHangGiao1: shipmentData.idKhachHangGiao1 || '',
    idKhachHangGiao2: shipmentData.idKhachHangGiao2 || '',
    idKhachHangGiao3: shipmentData.idKhachHangGiao3 || '',
    idKhachHangGiao4: shipmentData.idKhachHangGiao4 || '',
    idDiaChiLayHang: shipmentData.idDiaChiLayHang || '',
    idDiaChiGiaoHang1: shipmentData.idDiaChiGiaoHang1 || '',
    idDiaChiGiaoHang2: shipmentData.idDiaChiGiaoHang2 || '',
    idDiaChiGiaoHang3: shipmentData.idDiaChiGiaoHang3 || '',
    idDiaChiGiaoHang4: shipmentData.idDiaChiGiaoHang4 || '',
    diaChiLayHang: shipmentData.diaChiLayHang || '',
    diaChiGiaoHang1: shipmentData.diaChiGiaoHang1 || '',
    diaChiGiaoHang2: shipmentData.diaChiGiaoHang2 || '',
    diaChiGiaoHang3: shipmentData.diaChiGiaoHang3 || '',
    diaChiGiaoHang4: shipmentData.diaChiGiaoHang4 || '',
    lienhenhan: shipmentData.lienhenhan || '',
    linkmapnhan: shipmentData.linkmapnhan || '',
    lienhegiao1: shipmentData.lienhegiao1 || '',
    linkmapgiao1: shipmentData.linkmapgiao1 || '',
    lienhegiao2: shipmentData.lienhegiao2 || '',
    linkmapgiao2: shipmentData.linkmapgiao2 || '',
    lienhegiao3: shipmentData.lienhegiao3 || '',
    linkmapgiao3: shipmentData.linkmapgiao3 || '',
    lienhegiao4: shipmentData.lienhegiao4 || '',
    linkmapgiao4: shipmentData.linkmapgiao4 || '',
    gionhan1: shipmentData.gionhan1 || '',
    phutnhan1: shipmentData.phutnhan1 || '',
    giogiao1: shipmentData.giogiao1 || '',
    phutgiao1: shipmentData.phutgiao1 || '',
    giogiao2: shipmentData.giogiao2 || '',
    phutgiao2: shipmentData.phutgiao2 || '',
    giogiao3: shipmentData.giogiao3 || '',
    phutgiao3: shipmentData.phutgiao3 || '',
    giogiao4: shipmentData.giogiao4 || '',
    phutgiao4: shipmentData.phutgiao4 || '',
    dongia: shipmentData.dongia || shipmentData.giatri || '',
    trangthai: shipmentData.trangthai || '0',
    ghichu: shipmentData.ghichu || '',
    userName: shipmentData.userName || ''
  };

  // Debug log to check the data being sent
  console.log('API parameters being sent:', params);
  
  // // Additional validation to ensure loaivantai is not empty
  // if (!params.loaivantai || params.loaivantai === '') {
  //   params.loaivantai = '1'; // Default value
  //   console.warn('loaivantai was empty, setting default value: 1');
  // }
  
  const response = await post(API_ENDPOINTS.SAVE_SHIPMENT, params);
  return parseApiResponse(response);
  }

};

export const masterDataService = {
  /**
   * Get customers list
   * @returns {Promise} Customers response
   */
  getCustomers: async () => {
    const response = await post(API_ENDPOINTS.GET_CUSTOMERS, {
      keyPass: API_CONFIG.KEY_PASS,
      filter: "",
    });
    return parseApiResponse(response);
  },

  /**
   * Get drivers list
   * @returns {Promise} Drivers response
   */
  getDrivers: async () => {
    const response = await post(API_ENDPOINTS.GET_DRIVERS, {
      keyPass: API_CONFIG.KEY_PASS,
      idLaiXe: "",
      filter: "",
    });
    return parseApiResponse(response);
  },

  /**
   * Get payload list
   * @returns {Promise} Payload response
   */
  getPayload: async () => {
    const response = await post(API_ENDPOINTS.GET_PAYLOAD, {
      keyPass: API_CONFIG.KEY_PASS,
    });
    return parseApiResponse(response);
  },

  /**
   * Get trucks list
   * @param {number} idTrongTai - Payload ID (optional filter)
   * @returns {Promise} Trucks response
   */
  getTrucks: async (idTrongTai = null) => {
    const params = {
      keyPass: API_CONFIG.KEY_PASS,
    };
    if (idTrongTai) {
      params.idTrongTai = idTrongTai;
    } else {
      params.idTrongTai = "";
      params.filter = "";
    }
    const response = await post(API_ENDPOINTS.GET_TRUCKS, params);
    return parseApiResponse(response);
  },

  /**
   * Get shipping addresses
   * @param {string} idKhachHang - Customer code (optional filter)
   * @returns {Promise} Shipping addresses response
   */
  getShippingAddresses: async (idKhachHang = null) => {
    const params = {
      keyPass: API_CONFIG.KEY_PASS,
    };
    if (idKhachHang) {
      params.idKhachHang = idKhachHang;
    } else {
      params.idKhachHang = "";
      params.filter = "";
    }

    const response = await post(API_ENDPOINTS.GET_SHIPPING_ADDRESS, params);
    return parseApiResponse(response);
  },

  /**
   * Get price information
   * @param {object} priceParams - Price calculation parameters
   * @returns {Promise} Price response
   */
  getPrice: async () => {
    const params = {
      keyPass: API_CONFIG.KEY_PASS,
      idKhachHang: "",
      idDiaChiLayHang: "",
      idDiaChiGiaoHang: "",
      idXe: "",
    };

    const response = await post(API_ENDPOINTS.GET_PRICE, params);
    return parseApiResponse(response);
  },

  /**
   * Save shipment with detailed parameters (wrapper for shipmentService.saveShipment)
   * @param {object} shipmentData - Complete shipment data
   * @returns {Promise} Save response
   */
  saveShipment: async (shipmentData) => {
    return await shipmentService.saveShipment(shipmentData);
  }
};


export const imageService = {
  /**
   * Upload image to server
   * @param {string} userName - User name
   * @param {string} imageBase64 - Base64 encoded image
   * @param {string} fileName - File name
   * @param {string} imageType - Image type (default: 'shipment')
   * @param {string} idChuyenXe - Shipment ID
   * @param {number} trangthai - Status code (optional)
   * @returns {Promise} Upload response
   */
  uploadImage: async (userName, imageBase64, fileName, imageType = 'shipment', idChuyenXe, trangthai = null) => {
    try {
      const params = {
        keyPass: API_CONFIG.KEY_PASS,
        userName: userName,
        imageBase64: imageBase64,
        fileName: fileName,
        imageType: imageType,
        idChuyenXe: idChuyenXe,
      };
      
      // Add trangthai parameter if provided
      if (trangthai !== null) {
        params.trangthai = trangthai;
      }
      
      const response = await post(API_ENDPOINTS.UPLOAD_IMAGE, params);
      
      return parseApiResponse(response);
    } catch (error) {
      console.error('Image upload error:', error);
      return {
        success: false,
        message: error.message || 'Upload failed',
        data: null
      };
    }
  },

  /**
   * Delete image from server
   * @param {string} filePath - Path to the image file on server
   * @returns {Promise} Delete response
   */
  deleteImage: async (filePath) => {
    try {
      const response = await post(API_ENDPOINTS.DELETE_IMAGE, {
        keyPass: API_CONFIG.KEY_PASS,
        filePath: filePath
      });
      
      return parseApiResponse(response);
    } catch (error) {
      console.error('Image delete error:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete image',
        data: null
      };
    }
  },

  /**
   * Get images for a shipment
   * @param {number} idChuyenXe - Shipment ID
   * @returns {Promise} Images response
   */
  getShipmentImages: async (idChuyenXe) => {
    try {
      const response = await post(API_ENDPOINTS.GET_SHIPMENT_IMAGES, {
        keyPass: API_CONFIG.KEY_PASS,
        idChuyenXe: idChuyenXe
      });
      
      return parseApiResponse(response);
    } catch (error) {
      console.error('Get shipment images error:', error);
      return {
        success: false,
        message: error.message || 'Failed to get shipment images',
        data: null
      };
    }
  },
  

  /**
   * Convert file to base64
   * @param {File} file - File object
   * @returns {Promise<string>} Base64 string
   */
  fileToBase64: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data:image/jpeg;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  },

  /**
   * Resize image before upload
   * @param {File} file - Image file
   * @param {number} maxWidth - Maximum width
   * @param {number} maxHeight - Maximum height
   * @param {number} quality - Image quality (0-1)
   * @returns {Promise<string>} Resized base64 string
   */
  resizeImage: (file, maxWidth = 1024, maxHeight = 768, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = height * (maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      
      // Create object URL for the image
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * Validate image file before upload
   * @param {File} file - Image file
   * @returns {object} Validation result
   */
  validateImageFile: (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'File quá lớn. Kích thước tối đa là 5MB'
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Định dạng file không được hỗ trợ. Chỉ chấp nhận JPG, PNG, WebP'
      };
    }

    return { isValid: true };
  }
};

// Notification service for FCM token registration
export const notificationService = {
  /**
   * Register FCM token with the server
   * @param {string} idLaiXe - Driver ID
   * @param {string} idMay - FCM token
   * @returns {Promise} Registration response
   */
  registerFCMToken: async (idLaiXe, idMay) => {
    try {
      const response = await post(API_ENDPOINTS.SAVE_DEVICE, {
        keyPass: API_CONFIG.KEY_PASS,
        idLaiXe: idLaiXe,
        idMay: idMay
      });
      
      return parseApiResponse(response);
    } catch (error) {
      console.error('FCM token registration error:', error);
      return {
        success: false,
        message: error.message || 'Failed to register FCM token',
        data: null
      };
    }
  }
};

// Export the post function for direct use if needed
export { post };
